import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import type { MatterArtifactSelectionAttachment } from '@/agent-v2/artifact-selection/types'
import type { AgentV2MatterArtifactEditProposedPayload } from '@/agent-v2/store/schemas'
import { AgentV2ServerEventType } from '@/agent-v2/store/schemas'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'

export enum AgentV2MatterArtifactEditProposalPhase {
  READY = 'ready',
  ACCEPTING = 'accepting',
}

export type AgentV2MatterArtifactEditProposal = {
  conversationId: string
  originatingTurnId: string
  proposalId: string
  matterArtifactId: string
  sourceMatterArtifactVersionId: string
  /** created_at of the source head this proposal was computed against; null when it was unknown at receipt. */
  sourceCreatedAt: number | null
  replacementMarkdown: string
  editorRange: MatterArtifactSelectionAttachment['editorRange'] | null
  phase: AgentV2MatterArtifactEditProposalPhase
  acceptingOperation: AgentV2MatterArtifactEditProposedPayload['operation'] | null
}

export type AgentV2MatterArtifactEditProposalsState = {
  byConversationId: Record<string, AgentV2MatterArtifactEditProposal | undefined>
}

const initialState: AgentV2MatterArtifactEditProposalsState = {
  byConversationId: {},
}

const agentV2MatterArtifactEditProposalsSlice = createSlice({
  name: 'agentV2MatterArtifactEditProposalsState',
  initialState,
  reducers: {
    proposalReceived: (
      state,
      action: PayloadAction<{
        conversationId: string
        originatingTurnId: string
        proposal: AgentV2MatterArtifactEditProposedPayload
        editorRange?: MatterArtifactSelectionAttachment['editorRange']
        sourceCreatedAt?: number | null
      }>
    ) => {
      const { conversationId, originatingTurnId, proposal } = action.payload
      const existingProposal = state.byConversationId[conversationId]
      if (existingProposal?.proposalId === proposal.proposal_id) return

      state.byConversationId[conversationId] = {
        conversationId,
        originatingTurnId,
        proposalId: proposal.proposal_id,
        matterArtifactId: proposal.matter_artifact_id,
        sourceMatterArtifactVersionId: proposal.source_matter_artifact_version_id,
        sourceCreatedAt: action.payload.sourceCreatedAt ?? null,
        replacementMarkdown: proposal.replacement_markdown,
        editorRange: action.payload.editorRange ?? null,
        phase: AgentV2MatterArtifactEditProposalPhase.READY,
        acceptingOperation: null,
      }
    },
    acceptanceRequested: (
      state,
      action: PayloadAction<{
        conversationId: string
        operation: AgentV2MatterArtifactEditProposedPayload['operation']
        proposalId: string
      }>
    ) => {
      const proposal = state.byConversationId[action.payload.conversationId]
      if (proposal?.proposalId !== action.payload.proposalId || proposal.phase === AgentV2MatterArtifactEditProposalPhase.ACCEPTING) return

      proposal.phase = AgentV2MatterArtifactEditProposalPhase.ACCEPTING
      proposal.acceptingOperation = action.payload.operation
    },
    acceptanceSucceeded: (state, action: PayloadAction<{ conversationId: string; proposalId: string }>) => {
      const proposal = state.byConversationId[action.payload.conversationId]
      if (proposal?.proposalId === action.payload.proposalId) delete state.byConversationId[action.payload.conversationId]
    },
    acceptanceFailed: (state, action: PayloadAction<{ conversationId: string; proposalId: string }>) => {
      const proposal = state.byConversationId[action.payload.conversationId]
      if (proposal?.proposalId === action.payload.proposalId) delete state.byConversationId[action.payload.conversationId]
    },
    proposalRejected: (state, action: PayloadAction<{ conversationId: string; proposalId: string }>) => {
      const proposal = state.byConversationId[action.payload.conversationId]
      if (proposal?.proposalId === action.payload.proposalId && proposal.phase !== AgentV2MatterArtifactEditProposalPhase.ACCEPTING) {
        delete state.byConversationId[action.payload.conversationId]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AgentV2TranscriptActions.serverEventReceived, (state, action) => {
        if (action.payload.event.type !== AgentV2ServerEventType.RUN_FAILED && action.payload.event.type !== AgentV2ServerEventType.RUN_CANCELLED) return
        const proposal = state.byConversationId[action.payload.conversationId]
        if (proposal?.originatingTurnId === action.payload.event.turn_id) delete state.byConversationId[action.payload.conversationId]
      })
      .addCase(AgentV2ConversationsActions.setConversationDetail, (state, action) => {
        const conversationId = action.payload.conversation.id
        const proposal = state.byConversationId[conversationId]
        if (!proposal) return

        const originatingTurn = action.payload.turns.find((turn) => turn.id === proposal.originatingTurnId)
        if (originatingTurn?.did_fail || originatingTurn?.did_cancel) delete state.byConversationId[conversationId]
      })
      .addCase(MatterArtifactsActions.upsertMatterArtifactVersion, (state, action) => {
        if (!action.payload.is_current) return

        for (const [conversationId, proposal] of Object.entries(state.byConversationId)) {
          if (
            proposal?.matterArtifactId === action.payload.matter_artifact_id &&
            proposal.sourceMatterArtifactVersionId !== action.payload.id &&
            // A claim older than the proposal's source head cannot be a newer head — it is the same
            // stale-snapshot shape the matter-artifacts reducer rejects, so it must not retire a
            // proposal that is still valid against the real head.
            (proposal.sourceCreatedAt === null || action.payload.created_at >= proposal.sourceCreatedAt)
          ) {
            delete state.byConversationId[conversationId]
          }
        }
      })
      .addCase(AgentV2ConversationsActions.deleteConversation, (state, action) => {
        delete state.byConversationId[action.payload.conversationId]
      })
      .addCase(AgentV2ConversationsActions.nullifyData, () => initialState)
  },
})

export const AgentV2MatterArtifactEditProposalActions = agentV2MatterArtifactEditProposalsSlice.actions

export default agentV2MatterArtifactEditProposalsSlice.reducer
